import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { FormikProps, getIn } from "formik";
import { Typography } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { Add01Icon, Dollar02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Chip, TextField } from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import RemovableField from "components/common/RemovableField";
import type { CardProgramCapabilities } from "hooks/useCardPrograms";
import { CardPolicy } from "utils/cards/cardFormUtils";

export type LimitType =
  | "transactionLimit"
  | "dailyLimit"
  | "weeklyLimit"
  | "monthlyLimit"
  | "totalLimit";

interface Limit {
  value: LimitType;
  label: string;
  shortLabel: string;
}

interface SpendingLimitFieldsProps {
  formik: FormikProps<CardPolicy>;
  // An EXTERNAL card carries limits it does not own, so admin renders it read-only.
  disabled?: boolean;
  /** What the card's program can honor; a limit it cannot is not offered. */
  capabilities?: Partial<CardProgramCapabilities>;
}

const fieldError = (
  formik: FormikProps<CardPolicy>,
  field: string,
): string | undefined => {
  const touched = getIn(formik.touched, field);
  const error = getIn(formik.errors, field);
  return touched && typeof error === "string" ? error : undefined;
};

const SpendingLimitFields: React.FC<SpendingLimitFieldsProps> = ({
  formik,
  disabled = false,
  capabilities,
}) => {
  const { t } = useTranslation();

  const limits: Limit[] = useMemo(
    () => [
      {
        value: "transactionLimit" as const,
        label: t("Transaction Limit"),
        shortLabel: t("LimitTypeShort_transaction"),
      },
      {
        value: "dailyLimit" as const,
        label: t("Daily Limit"),
        shortLabel: t("LimitTypeShort_daily"),
      },
      {
        value: "weeklyLimit" as const,
        label: t("Weekly Limit"),
        shortLabel: t("LimitTypeShort_weekly"),
      },
      {
        value: "monthlyLimit" as const,
        label: t("Monthly Limit"),
        shortLabel: t("LimitTypeShort_monthly"),
      },
      {
        value: "totalLimit" as const,
        label: t("Total Card Limit"),
        shortLabel: t("LimitTypeShort_total"),
      },
    ],
    [t],
  );
  const availableLimits = useMemo(
    () => limits.filter((limit) => capabilities?.[limit.value] !== false),
    [limits, capabilities],
  );

  // Without weekly or monthly limits, the total is the card's overall amount, so its row is there from the start.
  const defaultLimits: LimitType[] = useMemo(
    () =>
      capabilities?.weeklyLimit === false &&
      capabilities?.monthlyLimit === false &&
      capabilities?.totalLimit !== false
        ? ["totalLimit"]
        : [],
    [capabilities],
  );

  // Start from limits that already carry a value, plus the program's default; a fresh form otherwise shows none.
  const seedLimits = useCallback(
    (): LimitType[] =>
      Array.from(
        new Set([
          ...availableLimits
            .filter((limit) => formik.values[limit.value])
            .map((limit) => limit.value),
          ...defaultLimits,
        ]),
      ),
    [availableLimits, defaultLimits, formik.values],
  );
  const [selectedLimits, setSelectedLimits] = useState<LimitType[]>(seedLimits);

  // Set on add, consumed by the new input's `inputRef` when it mounts.
  const pendingFocus = useRef<LimitType | null>(null);

  useEffect(() => {
    const withValues = availableLimits
      .filter((limit) => formik.values[limit.value])
      .map((limit) => limit.value);
    setSelectedLimits((prev) => {
      const merged = Array.from(new Set([...prev, ...withValues]));
      // A fresh array every run loops against the DS field's own InputBase effect.
      return merged.length === prev.length ? prev : merged;
    });
  }, [availableLimits, formik.values]);

  // The rows start over when the form is refilled: resetForm swaps initialValues, and picking or clearing a preset swaps presetId.
  const seededFrom = useRef({
    initialValues: formik.initialValues,
    presetId: formik.values.presetId,
  });
  useEffect(() => {
    const seeded = seededFrom.current;
    if (
      seeded.initialValues === formik.initialValues &&
      seeded.presetId === formik.values.presetId
    )
      return;
    seededFrom.current = {
      initialValues: formik.initialValues,
      presetId: formik.values.presetId,
    };
    setSelectedLimits(seedLimits());
  }, [seedLimits, formik.initialValues, formik.values.presetId]);

  // A new program drops the rows it cannot honor and brings its default; a removed default stays removed until then.
  const supportKey = `${availableLimits.map((limit) => limit.value).join()}|${defaultLimits.join()}`;
  const reconciledFor = useRef(supportKey);
  useEffect(() => {
    if (reconciledFor.current === supportKey) return;
    reconciledFor.current = supportKey;
    setSelectedLimits((prev) =>
      Array.from(
        new Set([
          ...prev.filter((limitType) =>
            availableLimits.some((limit) => limit.value === limitType),
          ),
          ...defaultLimits,
        ]),
      ),
    );
  }, [supportKey, availableLimits, defaultLimits]);

  const addLimit = (limitType: LimitType) => {
    setSelectedLimits((prev) =>
      prev.includes(limitType) ? prev : [...prev, limitType],
    );
    pendingFocus.current = limitType;
  };

  const removeLimit = (limitType: LimitType) => {
    setSelectedLimits((prev) => prev.filter((limit) => limit !== limitType));
    formik.setFieldValue(limitType, "");
  };

  const unusedLimits = availableLimits.filter(
    (limit) => !selectedLimits.includes(limit.value),
  );

  // A read-only section with nothing set has to say so, or it renders empty.
  if (disabled && selectedLimits.length === 0) {
    return (
      <Typography variant="body1">
        {t("No spending limits are available")}
      </Typography>
    );
  }

  return (
    <Stack gap="var(--space-4)">
      {selectedLimits.map((limitType) => {
        const label =
          availableLimits.find((limit) => limit.value === limitType)?.label ??
          "";
        return (
          <RemovableField
            key={limitType}
            removeLabel={t("Delete")}
            disabled={disabled}
            onRemove={disabled ? undefined : () => removeLimit(limitType)}
          >
            <TextField
              fullWidth
              type="number"
              name={limitType}
              label={label}
              disabled={disabled}
              leading={<HugeiconsIcon icon={Dollar02Icon} size={14} />}
              // ref, not inputRef: the DS TextField overwrites inputRef with its own.
              ref={(node: HTMLInputElement | null) => {
                if (node && pendingFocus.current === limitType) {
                  pendingFocus.current = null;
                  node.focus();
                }
              }}
              value={formik.values[limitType] || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              errorText={fieldError(formik, limitType)}
              inputProps={{
                min: 0,
                // Stop the scroll wheel incrementing the amount.
                onWheel: (event: React.WheelEvent<HTMLInputElement>) =>
                  event.currentTarget.blur(),
              }}
            />
          </RemovableField>
        );
      })}

      {unusedLimits.length > 0 && !disabled && (
        <Inline gap="var(--space-2)">
          {unusedLimits.map((limit) => (
            <Chip
              key={limit.value}
              size="sm"
              tone="default"
              leading={<HugeiconsIcon icon={Add01Icon} size={16} />}
              // The pill must announce which limit it adds; the noun alone is not an action.
              aria-label={t("AddSpendingLimit", { limit: limit.label })}
              // The DS Chip derives clickable and tabIndex from onClick.
              onClick={() => addLimit(limit.value)}
            >
              {limit.shortLabel}
            </Chip>
          ))}
        </Inline>
      )}
    </Stack>
  );
};

export default SpendingLimitFields;
